import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import AppConstants from "services/constants/AppConstants";
import {
  applyCoupon,
  getAnalytics,
  getAvailableSlots,
  getBankAccountDetails,
  getBookingDetails,
  getDashboardStats,
  getForYouTemplates,
  getNextSteps,
  getProfile,
  getPublicServices,
  getRescheduleBookingSlots,
  getRescheduleServices,
  getSampleServices,
  getShareTemplates,
  getSharingInfo,
  getTopEarners,
  getTransactionDetails,
  getUserByUsername,
  getUserByUsernameAdditional,
  getUserStats,
  updateProfile,
  updateService,
} from "services/dashboard-services";
import {
  apiSocialAuth,
  getAwardCategories,
  getNominatedUsers,
  getUseCaseCategories,
} from "services/public-services";
import { getOrSetPersonType } from "utils/Analytics/ABTestingHelper";

export const APIStateKeysEnum = {
  Profile: "profile",
  Stats: "user_stats",
  UserByUserName: "userByUserName",
  UserByUserNameAdditional: "userByUserNameAdditional",
  UserByUserNameLite: "userByUserNameLite",
  PublicService: "service_public",
  Coupon: "coupon",
  ShareTemplates: "shareTemplates",
  ShareTemplatesV2: "shareTemplatesV2",
  ShareData: "shareData",
  UserStats: "userStats",
  UserDashboardStats: "userDashboardStats",
  CheckSteps: "checkSteps",
  AccountingDetails: "paymentsData",
  WithdrawMoney: "withdrawMoney",
  UseCaseCategories: "useCaseCategories",
  NominatedUsers: "nominatedUsers",
  CreatorAwardCategories: "creatorAwardCategories",
  BankAccountStatus: "bankAccountStatus",
  BankAccountDetails: "bank_details",
  ServiceDetails: "serviceDetails",
  Schedules: "schedules",
  Country: "country",
  SampleServices: "sampleServices",
  Referral: "referral",
  DashboardService: "service_dashboard",
  DashboardQueries: "dashboard_queries",
  AIPosts: "ai_posts",
  ShareCategories: "share_categories",
  TransactionDetails: "transactionDetails",
  BookingDetails: "booking_details",
  ExpertCategories: "expert_categories",
  PublicTestimonials: "public_testimonials",
  TopEarners: "top_earners",
  ForYouTemplates: "for_you_templates",
  BillboardData: "billboard",
  AvatarList: "avatar_list",
  ThanksGivingMessages: "thanksgiving",
  SmartDiscount: "smart_discount",
  ReferralDashboard: "referral_dashboard",
  DashboardTestimonials: "dashboard_testimonials",
  PublicBookingReviews: "public-booking-reviews",
  ServiceCheckout: "service-checkout",
  RecordingEnabled: "recording_enabled",
};

export const useProfile = (enabled = true): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.Profile],
    queryFn: getProfile,
    staleTime: 1800000,
    enabled: enabled ? true : false,
    retry: false,
  });

  return {
    user: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useStats = (): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.Stats],
    queryFn: getUserStats,
    staleTime: 1800000,
  });

  return {
    stats: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useBankAccountDetails = (): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.BankAccountDetails],
    queryFn: getBankAccountDetails,
    staleTime: 300000,
  });

  return {
    bank_details: data,
    error: error,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useGetUserSlots = (
  service_id,
  user_id,
  interval,
  bookingID
): any => {
  const { data, error, isLoading, isSuccess, isStale, status, isRefetching } =
    useQuery({
      queryKey: ["slots", service_id, user_id, interval, bookingID],
      queryFn: (): any =>
        bookingID
          ? getRescheduleBookingSlots(bookingID)
          : getAvailableSlots(service_id, user_id, interval),
      staleTime: 120000,
    });

  return {
    slots: data?.data,
    error: error,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
    isRefetching: isRefetching,
  };
};

export const useGetServicePublic = (service_id, bookingID): any => {
  const { data, error, isLoading, isSuccess, isStale, status, isRefetching } =
    useQuery({
      queryKey: ["service_public", service_id, bookingID ? bookingID : null],
      queryFn: (): any =>
        bookingID
          ? getRescheduleServices(bookingID)
          : getPublicServices(service_id),
      staleTime: 180000,
      enabled: service_id ? true : false,
      retry: false,
      refetchOnWindowFocus: false,
    });

  return {
    service: data,
    error: error,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
    isRefetching: isRefetching,
  };
};

export const useGetUserByUsername = (username, skip_cache = false): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.UserByUserName, username],
    queryFn: (): any => getUserByUsername(username, null, skip_cache),
    staleTime: 360000,
    enabled: username ? true : false,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return {
    user: data,
    error: error,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useGetUserByUsernameAdditional = (username, variant): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.UserByUserNameAdditional, username, variant],
    queryFn: (): any => getUserByUsernameAdditional(username, variant),
    staleTime: 360000,
    enabled: username && ["A", "B", "a", "b"]?.includes(variant) ? true : false,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return {
    user: data,
    error: error,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useGetShareTemplates = (): any => {
  const {
    data,
    error,
    isLoading,
    isSuccess,
    isStale,
    status,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: [APIStateKeysEnum.ShareTemplates],
    queryFn: async () => await getShareTemplates(),
    staleTime: 180000,
  });

  return {
    templates: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
    isRefetching: isRefetching,
    refetch: refetch,
  };
};

export const useGetShareData = (): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.ShareData],
    queryFn: async () => await getSharingInfo(),
    staleTime: 180000,
  });

  return {
    shareData: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useBookingDetails = (booking_id): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.BookingDetails],
    queryFn: async () => await getBookingDetails(booking_id),
    staleTime: 180000,
    enabled: booking_id ? true : false,
  });

  return {
    bookingData: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useNextStepsData = (variant, enabled = true): any => {
  const dashboardActivationAB: any = getOrSetPersonType(
    AppConstants?.DASHBOARD_HOME_ACTIVATION_AB
  );
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [
      APIStateKeysEnum.CheckSteps,
      variant || dashboardActivationAB || "A",
    ],
    queryFn: async () =>
      await getNextSteps(variant || dashboardActivationAB || "A"),
    staleTime: 30000,
    enabled: enabled ? true : false,
  });

  return {
    checkSteps: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useUserStats = (time): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.UserStats, time],
    queryFn: async () => await getAnalytics(time),
    staleTime: 180000,
  });

  return {
    userStats: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useUserDashboardStats = (time): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.UserDashboardStats, time],
    queryFn: async () => await getDashboardStats(time),
    staleTime: 180000,
  });

  return {
    userDashboardStats: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useServiceTemplates = (type): any => {
  const { data, error, isLoading, isSuccess, isStale, status } = useQuery({
    queryKey: [APIStateKeysEnum.SampleServices],
    queryFn: async () => await getSampleServices(type),
    staleTime: 180000,
  });

  return {
    sampleService: data,
    isLoading: isLoading,
    isError: error,
    isSuccess: isSuccess,
    isStale: isStale,
    status: status,
  };
};

export const useCategoriesUseCase = (id): any => {
  const { data, error, isLoading, status } = useQuery({
    queryKey: [APIStateKeysEnum.UseCaseCategories, id],
    queryFn: async () => await getUseCaseCategories(id),
    staleTime: 180000,
  });

  return {
    categories: id !== 0 ? data : data,
    isLoading: isLoading,
    isError: error,
    status: status,
  };
};

export const useNominatedUsers = (id, page): any => {
  const { data, error, isLoading, status } = useQuery({
    queryKey: [APIStateKeysEnum.NominatedUsers, id, page],
    queryFn: async () => await getNominatedUsers(id, page),
    staleTime: 180000,
  });

  return {
    nominatedUsers: id !== 0 ? data : data,
    isLoading: isLoading,
    isError: error,
    status: status,
  };
};

export const useCreatorAwardCategories = (): any => {
  const { data, error, isLoading, status } = useQuery({
    queryKey: [APIStateKeysEnum.CreatorAwardCategories],
    queryFn: async () => await getAwardCategories(),
    staleTime: 180000,
  });

  return {
    categories: data,
    isLoading: isLoading,
    isError: error,
    status: status,
  };
};

export const useUpdateProfile = (): any => {
  return useMutation({
    mutationFn: (obj) => updateProfile(obj),
  });
};

export const useUpdateService = (): any => {
  return useMutation({
    mutationFn: (obj) => updateService(obj),
  });
};

export const useUpdateCoupon = (service_id): any => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (obj) => applyCoupon(obj),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [APIStateKeysEnum.Coupon, service_id],
      });
    },
  });
};

export const useSocialSignin = (): any => {
  return useMutation({
    mutationFn: (obj) => apiSocialAuth(obj),
  });
};

export const useTransactionData = (
  type: any = [1, 2, 3, 4, 5, 6, 7],
  service: any = null,
  month: any = null,
  page: any = 1
): any => {
  const { data, error, isLoading, status } = useQuery({
    queryKey: [
      APIStateKeysEnum.TransactionDetails,
      [type, page, service, month],
    ],
    queryFn: async () =>
      await getTransactionDetails(type, service, month, page),
    staleTime: 300000,
    placeholderData: keepPreviousData,
  });

  return {
    transactionData: data,
    isLoading: isLoading,
    isError: error,
    status: status,
  };
};

export const useTopEarners = (type): any => {
  const { data, error, isLoading, status } = useQuery({
    queryKey: [APIStateKeysEnum.TopEarners, type],
    queryFn: async () => await getTopEarners(type),
    staleTime: 180000,
    placeholderData: keepPreviousData,
  });

  return {
    topEarners: data,
    isLoading: isLoading,
    isError: error,
    status: status,
  };
};

export const useForYouSharing = (): any => {
  const { data, error, isLoading, status } = useQuery({
    queryKey: [APIStateKeysEnum.ForYouTemplates],
    queryFn: async () => await getForYouTemplates(),
    staleTime: 180000,
    placeholderData: keepPreviousData,
  });

  return {
    forYouTemplates: data,
    isLoading: isLoading,
    isError: error,
    status: status,
  };
};
